/**
 * URL Decode Tool
 */

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const UrlDecodeTool: ITransformerTool = {
  id: 'url-decode',
  name: 'URL Decode',
  description: 'Decode URL-encoded text',
  version: '1.0.0',
  type: 'transformer',
  category: 'Encoders & Decoders',
  keywords: ['url', 'decode', 'percent', 'uri', 'encoding'],

  seo: {
    slug: 'url-decode',
    pageTitle: 'URL Decoder - Free Online Percent-Decoding Tool',
    metaDescription: 'Decode percent-encoded URL strings back to readable text. Handles Unicode and special characters. Fast, private, runs entirely in your browser.',
    keywords: ['url decode', 'url decoder', 'percent decoding', 'uri decode online'],
    relatedTools: ['url-encode', 'base64-decode'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.URL_ENCODED, SEMANTIC_TYPES.TEXT],
  outputType: SEMANTIC_TYPES.TEXT,

  attributes: [
    {
      name: 'decodePlus',
      type: 'boolean',
      label: 'Decode + as Space',
      defaultValue: false,
      description: 'Decode + as space (standard for application/x-www-form-urlencoded)',
    },
  ],

  supportsInput(input: string, detectedType?: string): boolean {
    // Prioritize URL_ENCODED, but accept TEXT too
    if (detectedType === 'URLEncoded') return true;
    // Check if it looks URL encoded
    return /%[0-9A-Fa-f]{2}/.test(input);
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    try {
      const decodePlus = config.decodePlus === true;
      const processed = decodePlus ? input.replace(/\+/g, ' ') : input;
      return decodeURIComponent(processed);
    } catch (e) {
      throw ToolError.validation('Invalid URL-encoded string', {
        suggestion: 'Check for malformed percent-encoded sequences',
      });
    }
  },
};

